/**
 * Test Email Modal Component
 * Send test emails to preview email templates/campaigns
 *
 * Created: January 4, 2026
 */

import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { sendTestEmail } from "../../redux/email-broadcast/emailBroadcastApi";

const TestEmailModal = ({
  show,
  onHide,
  token,
  templateId = null,
  campaignId = null,
  subject = "",
  htmlBody = "",
  fromName = "WTF Gyms",
  fromEmail = "noreply@wtfgyms.com",
  userEmail = "", // Pre-fill with logged-in user's email
}) => {
  const [testEmail, setTestEmail] = useState(userEmail || "");
  const [loading, setLoading] = useState(false);

  // Update email when modal opens with new user email
  useEffect(() => {
    if (show && userEmail) {
      setTestEmail(userEmail);
    }
  }, [show, userEmail]);

  const handleSendTest = async (e) => {
    e.preventDefault();

    if (!testEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(testEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        to_email: testEmail,
        template_id: templateId,
        campaign_id: campaignId,
        subject: subject,
        html_body: htmlBody,
        from_name: fromName,
        from_email: fromEmail,
      };

      const response = await sendTestEmail(payload, token);

      if (response?.status) {
        toast.success(`Test email sent to ${testEmail}`);
        onHide();
      } else {
        toast.error(response?.message || "Failed to send test email");
      }
    } catch (error) {
      console.error("Test email error:", error);
      toast.error(error.message || "Failed to send test email");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Send Test Email</Modal.Title>
      </Modal.Header>

      <form onSubmit={handleSendTest}>
        <Modal.Body>
          <div className="mb-3">
            <p className="text-muted small mb-3">
              Send a test email to preview how it will look in the inbox. The email will include a
              <strong> [TEST] </strong> banner at the top.
            </p>

            <Form.Group>
              <Form.Label>
                Recipient Email Address <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="your.email@example.com"
                required
                autoFocus
                disabled={loading}
              />
              <Form.Text className="text-muted">
                We'll send a preview to this email address
              </Form.Text>
            </Form.Group>

            {subject && (
              <div className="mt-3 p-2 bg-light rounded">
                <small className="text-muted d-block mb-1">Subject Preview:</small>
                <div className="fw-medium">[TEST] {subject}</div>
              </div>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={loading || !testEmail.trim()}
            style={{ backgroundColor: "#D2000B", borderColor: "#D2000B" }}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Sending...
              </>
            ) : (
              "Send Test Email"
            )}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default TestEmailModal;
